import Image from "next/image";
import Favicon from "@/components/favicon";
import { Post } from "@/lib/blogApi";
import { SanityImage, urlForImage } from "@/lib/sanity";
import VimeoPlayer from "../vimeo";

export const postHeroImageDimensions = [1600, 800] as const;
const [width, height] = postHeroImageDimensions;
export const postHeroImageSize = { width, height } as const;

export const postHeroImageUrl = (img: SanityImage) =>
  urlForImage(img).width(width).height(height).url();

export const postSocialImageUrl = (
  socialImg: SanityImage,
  heroImg: SanityImage
) =>
  socialImg?.asset
    ? urlForImage(socialImg).url()
    : urlForImage(heroImg).width(width).height(height).url();

const formatDate = (date: string) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

function BlogPostHeader({
  post,
  heading: Heading,
}: {
  post: Post;
  heading: "h1" | "h2";
}) {
  return (
    <>
      {post.customerImg && post.customerName && (
        <section
          className="customer-header"
          role="img"
          aria-label={`${post.customerName} + Watershed logos`}
        >
          <Image
            src={urlForImage(post.customerImg).width(180).height(42).url()}
            alt={post.customerName}
            height={42}
            width={180}
            objectFit="contain"
            objectPosition="right center"
            aria-hidden
          />
          <span aria-hidden>+</span>
          <div
            style={{ width: 90, lineHeight: 0 /* optical alignment */ }}
            aria-hidden
          >
            <Favicon width={42} height={42} />
          </div>
        </section>
      )}
      {post.videoPlayerUrl && Heading === "h1" ? (
        <div className="hero-container">
          <VimeoPlayer video={post.videoPlayerUrl} />
        </div>
      ) : post.heroImg?.asset ? (
        <div className="hero-container">
          <Image
            src={urlForImage(post.heroImg)
              .fit("crop")
              .crop("focalpoint")
              .width(1600)
              .height(800)
              .url()}
            alt={post.heroImg.alt}
            {...postHeroImageSize}
            className="hero-image"
          />
        </div>
      ) : null}
      <Heading className="blog-post-title">{post.title}</Heading>
      {post.updatedDate ? (
        <p className="blog-post-date">
          Published <time dateTime={post.date}>{formatDate(post.date)}</time>
          {" • "}
          Last updated{" "}
          <time dateTime={post.updatedDate}>
            {formatDate(post.updatedDate)}
          </time>
        </p>
      ) : (
        <time dateTime={post.date} className="blog-post-date">
          {formatDate(post.date)}
        </time>
      )}
      <style jsx>{`
        .customer-header {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 0 auto 48px;
        }

        .customer-header span {
          color: #222c49;
          opacity: 0.25;
          padding: 0 24px;
          font-size: 32px;
        }

        .hero-container {
          background-color: #edf1f5;
          max-width: 100vw;
          margin: 0 -12px 24px;
          line-height: 0;
        }

        .hero-image {
          width: 100%;
          height: 100%;
        }

        @media screen and (min-width: 520px) {
          .customer-header {
            margin: 32px auto 80px;
          }

          .hero-container {
            margin: 0 -48px 48px;
          }
          .hero-container:first-child {
            margin: -48px -48px 48px;
          }
        }
      `}</style>
    </>
  );
}

export default BlogPostHeader;
